/**
 * Lock State Calculation
 * 
 * Calculates lock states for tasks based on their unlock conditions.
 * 
 * Supports v2 block-based conditions (AND/OR at both block and condition level)
 * and legacy v1 flat array format (auto-converted to v2 for evaluation).
 * 
 * Also supports nested sprints - a task with a nested sprint at 100% progress
 * is considered "completed" for dependency purposes.
 * 
 * EXCLUDED STATE:
 * Tasks on non-selected decision branches are marked as "excluded".
 * This is different from "locked" - excluded means "this path wasn't chosen"
 * while locked means "waiting for something to complete".
 */

import { evaluateUnlockConditions } from './evaluateConditions';
import { getRelevantTasksForProgress } from '../progressUtils';
import { getEffectiveConditionsV2, hasAnyConditions } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';
import { formatWaitAfterHint, formatWaitCountdown } from '../../../shared/ConditionsDropdown/utils/conditionWait';

/**
 * Calculate lock state for all tasks
 * @param {Array} tasks - Array of task objects
 * @param {Array} nestedSprints - Array of nested sprint objects (optional)
 * @param {Array} statuses - Custom statuses array (optional)
 * @returns {Object} Map of taskId -> { isLocked, isExcluded, lockReason, blockingTasks, usesConditions }
 */
export function calculateLockStates(tasks, nestedSprints = [], statuses = [], now = Date.now()) {
  const taskMap = new Map(tasks.map(t => [t.id, t]));
  const lockStates = {};
  const visiting = new Set(); // For cycle detection
  
  // Get excluded task IDs (tasks on non-selected decision branches)
  const { excludedTaskIds } = getRelevantTasksForProgress(tasks);

  // Helper to get lock state recursively with cycle detection
  function getLockState(taskId) {
    // Already calculated
    if (lockStates[taskId]) {
      return lockStates[taskId];
    }

    // Cycle detection - if we're already visiting this node, it's part of a cycle
    if (visiting.has(taskId)) {
      return { isLocked: false, lockReason: null, blockingTasks: [], usesConditions: false };
    }

    const task = taskMap.get(taskId);
    if (!task) {
      return { isLocked: false, lockReason: null, blockingTasks: [], usesConditions: false };
    }

    visiting.add(taskId);

    // ═══════════════════════════════════════════════════════════════
    // Get effective unlock conditions (handles v1, v2, and dependsOn)
    // ═══════════════════════════════════════════════════════════════
    const effectiveConditions = getEffectiveConditionsV2(task);

    let blockingTasks = [];
    let lockReason = null;
    let usesConditions = false;

    // ═══════════════════════════════════════════════════════════════
    // Evaluate conditions (v2 block-based with configurable AND/OR)
    // ═══════════════════════════════════════════════════════════════
    
    if (hasAnyConditions(effectiveConditions)) {
      usesConditions = true;
      
      const { isSatisfied, unsatisfiedTasks } = evaluateUnlockConditions(
        effectiveConditions,
        taskMap,
        nestedSprints,
        statuses,
        now
      );

      if (!isSatisfied) {
        blockingTasks = unsatisfiedTasks;
        lockReason = buildLockReason(blockingTasks);
      }
    }

    visiting.delete(taskId);

    const isLocked = blockingTasks.length > 0;
    const isExcluded = excludedTaskIds.has(taskId);
    
    lockStates[taskId] = {
      isLocked,
      isExcluded,
      lockReason: isLocked ? lockReason : null,
      blockingTasks,
      usesConditions,
    };

    return lockStates[taskId];
  }

  // Calculate lock state for all tasks
  for (const task of tasks) {
    getLockState(task.id);
  }

  return lockStates;
}

function buildLockReason(blockingTasks) {
  if (!blockingTasks.length) return null;

  const waiting = blockingTasks.filter((t) => t.reason === 'waiting_until');
  const others = blockingTasks.filter((t) => t.reason !== 'waiting_until');

  if (waiting.length > 0 && others.length === 0) {
    const remaining = Math.max(...waiting.map((t) => t.remainingMs || 0));
    const countdown = formatWaitCountdown(remaining);
    return countdown ? `Available in ${countdown}` : 'Available soon';
  }

  const pendingWaitHints = others
    .map((t) => formatWaitAfterHint(t.waitSeconds, t.name))
    .filter(Boolean);
  if (waiting.length === 0 && pendingWaitHints.length > 0 && pendingWaitHints.length === others.length) {
    return pendingWaitHints.length === 1
      ? pendingWaitHints[0]
      : pendingWaitHints.join('; ');
  }

  if (blockingTasks.length === 1) {
    const blocker = blockingTasks[0];
    if (blocker.reason === 'wait_unfulfilled') {
      return `Waiting for "${blocker.name}" to be fulfilled`;
    }
    if (blocker.requiredBranch) {
      return `Waiting for "${blocker.name}" → ${blocker.requiredBranch}`;
    }
    return `Waiting for "${blocker.name}" to be completed`;
  }

  const formatBlocker = (t) => {
    if (t.requiredBranch) return `"${t.name}" → ${t.requiredBranch}`;
    return `"${t.name}"`;
  };
  const names = blockingTasks.slice(0, 2).map(formatBlocker).join(' and ');
  const extra = blockingTasks.length > 2 ? ` (+${blockingTasks.length - 2} more)` : '';
  return `Waiting for ${names}${extra}`;
}
